import React, { useState, useRef, useEffect } from 'react';
import { FaTimes, FaPaperPlane, FaSpinner, FaCalculator, FaLightbulb, FaCocktail, FaDollarSign, FaThermometerHalf, FaTools, FaCogs, FaMicrophone } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { API } from '../App';
import axios from 'axios';
import OnboardingTooltip from './OnboardingTooltip';
import { getAiAssistantSteps, isTourCompleted, markTourCompleted, TOUR_KEYS } from '../utils/onboarding';
import { getAIConfig, filterTabs } from '../utils/aiContext';
import UpgradeModal from './UpgradeModal';
import { hasPremiumAccess } from '../config/subscriptions';

const AIMultiTabPopup = ({ isOpen, onClose, context = 'default' }) => {
  const { t } = useTranslation();
  const { user, updateCompletedTours } = useAuth();

  // Guests can OPEN the panel and explore but cannot send real prompts.
  const isGuest = !hasPremiumAccess(user);

  // Get context-specific configuration
  const aiConfig = getAIConfig(context);

  const [activeTab, setActiveTab] = useState(aiConfig.activeTab);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(-1);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const messagesEndRef = useRef(null);
  const tourInitialized = useRef(false);

  // Define all AI tabs with their configurations
  const tabs = [
    { 
      id: 'brix', 
      label: t('ai.tabs_brix', 'Brix Assistent'), 
      endpoint: '/ai/brix',
      icon: <FaCalculator />,
      placeholder: t('ai.brix.placeholder', 'F.eks. "300ml jordbær sirup og 700ml vand"'),
      introKey: 'ai.intro.brix',
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      id: 'create', 
      label: t('ai.tabs_create', 'Lav Opskrift'), 
      endpoint: '/ai/create-recipe',
      icon: <FaLightbulb />,
      placeholder: t('ai.recipe_create.placeholder', 'F.eks. "Lav en tropisk gul slush med ananas"'),
      introKey: 'ai.intro.create',
      color: 'from-green-500 to-emerald-500'
    },
    { 
      id: 'help', 
      label: t('ai.tabs_help', 'Tips & Tricks'), 
      endpoint: '/ai/help',
      icon: <FaLightbulb />,
      placeholder: t('ai.help.placeholder', 'Spørg AI om slush, maskiner eller fejlfinding...'),
      introKey: 'ai.intro.help',
      color: 'from-purple-500 to-pink-500'
    },
    { 
      id: 'machine', 
      label: t('ai.machines', 'Maskiner'), 
      endpoint: '/ai/machine',
      icon: <FaCogs />,
      placeholder: 'Spørg om maskine-kapacitet, frysetider eller problemer...',
      introKey: 'ai.intro.machine',
      color: 'from-gray-600 to-gray-800'
    },
    { 
      id: 'recommend', 
      label: t('ai.recommend', 'Anbefalinger'), 
      endpoint: '/ai/machine-recommend',
      icon: <FaTools />,
      placeholder: 'Beskriv dit behov – få maskine-anbefalinger...',
      introKey: 'ai.intro.recommend',
      color: 'from-orange-500 to-red-500'
    },
    { 
      id: 'freeze', 
      label: t('freeze.title', 'Temperatur'), 
      endpoint: '/ai/freeze-curve',
      icon: <FaThermometerHalf />,
      placeholder: 'Spørg om frysepunkt og konsistens...',
      introKey: 'ai.intro.freeze',
      color: 'from-blue-400 to-blue-600'
    },
    { 
      id: 'cost', 
      label: t('cost.title', 'Kostpris'), 
      endpoint: '/ai/cost-calc',
      icon: <FaDollarSign />,
      placeholder: 'Beregn kostpris for din opskrift...',
      introKey: 'ai.intro.cost',
      color: 'from-green-600 to-green-800'
    },
    { 
      id: 'troubleshoot', 
      label: t('ai.troubleshoot', 'Fejlfinding'), 
      endpoint: '/ai/machine-troubleshoot',
      icon: <FaTools />,
      placeholder: 'Beskriv problemet med din maskine...',
      introKey: 'ai.intro.troubleshoot',
      color: 'from-red-500 to-orange-500'
    },
    { 
      id: 'cocktail', 
      label: t('alcohol.title', 'Cocktail'), 
      endpoint: '/ai/alcohol-optimize',
      icon: <FaCocktail />,
      placeholder: 'Spørg om alkohol-optimering...',
      introKey: 'ai.intro.cocktail',
      color: 'from-pink-500 to-purple-600'
    }
  ];

  // Filter tabs based on context
  const availableTabs = filterTabs(tabs, aiConfig.availableTabs);
  const currentTab = availableTabs.find(t => t.id === activeTab) || availableTabs[0];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Set active tab based on context when popup opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(aiConfig.activeTab);
      setMessages([]);
      setInput('');
    }
  }, [isOpen, context]);

  // Clear messages when switching tabs
  useEffect(() => {
    setMessages([]);
    setInput('');
  }, [activeTab]);

  // Initialize voice recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = false;
      recognitionInstance.interimResults = false;
      recognitionInstance.lang = 'da-DK'; // Danish
      
      recognitionInstance.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(prev => prev + (prev ? ' ' : '') + transcript);
        setIsListening(false);
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      
      recognitionInstance.onend = () => {
        setIsListening(false);
      };
      
      setRecognition(recognitionInstance);
    }
  }, []);

  // Start AI assistant tour on first open
  useEffect(() => {
    // Only show tour if: popup is open, user is logged in (not guest), tour not completed
    if (isOpen && hasPremiumAccess(user) && !isTourCompleted(TOUR_KEYS.AI_ASSISTANT, user)) {
      // Only initialize tour once per popup open
      if (!tourInitialized.current) {
        console.log('[Tour] Starting AI assistant tour...');
        tourInitialized.current = true;
        setTimeout(() => {
          setCurrentTourStep(0);
        }, 800);
      }
    } else if (!isOpen) {
      // Reset when popup closes so it can show again next time (if still not completed)
      tourInitialized.current = false;
    }
  }, [isOpen, user]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    // 🔒 Guest gating — show a friendly inline "this is a Pro feature" reply
    // in the chat and pop the UpgradeModal. NO request hits the backend.
    if (isGuest) {
      const userMessage = input.trim();
      setInput('');
      setMessages(prev => [
        ...prev,
        { role: 'user', content: userMessage },
        {
          role: 'assistant',
          content: t(
            'ai.guestBlocked',
            'AI-assistenten er en betalt funktion. Som gæst kan du se hvordan den fungerer, men selve svarene kræver Light eller Pro. Klik på "Se abonnementer" nedenfor for at komme i gang.'
          ),
          isGuestPrompt: true,
        },
      ]);
      setShowUpgrade(true);
      return;
    }

    const userMessage = input.trim();
    setInput('');

    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}${currentTab.endpoint}`, {
        query: userMessage,
        active_tool: activeTab,
        language: 'da' // Can be dynamic based on user's locale
      });

      // Handle different response types
      let displayContent, rawData;
      
      if (response.data.recipe) {
        // create-recipe endpoint
        const recipe = response.data.recipe;
        displayContent = `✅ Opskrift genereret!\n\n📝 ${recipe.name}\n\n${recipe.description}\n\n📊 Brix: ${recipe.brix}°Bx\n📏 Volumen: ${recipe.base_volume_ml}ml\n\n🥄 Ingredienser: ${recipe.ingredients?.length || 0}\n📝 Trin: ${recipe.procedure?.length || 0}`;
        rawData = recipe;
      } else if (response.data.response) {
        // Text response endpoints (brix, help, etc.)
        displayContent = response.data.response;
        rawData = response.data;
      } else if (response.data.success) {
        // Structured response endpoints (freeze, cost, alcohol, etc.)
        displayContent = formatStructuredResponse(response.data);
        rawData = response.data;
      } else {
        displayContent = JSON.stringify(response.data, null, 2);
        rawData = response.data;
      }
      
      // Add AI response
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: displayContent,
        data: rawData
      }]);
    } catch (error) {
      console.error('AI Error:', error);

      // Check if it's a fallback error needing more info
      const errorData = error.response?.data;
      const status = error.response?.status;
      let errorMessage = t('ai.error', 'Der opstod en fejl. Prøv igen.');

      // Special-case the premium-gate on /api/ai/create-recipe (403 with a
      // structured detail object). Show a friendly upsell instead of the
      // raw object that previously stringified to "[object Object]".
      const detail = errorData?.detail;
      const premiumGate = (
        status === 403 &&
        ((errorData?.error === 'premium-required') ||
         (errorData?.error === 'pro-required') ||  // legacy fallback
         (detail && typeof detail === 'object' &&
          (detail.error === 'premium-required' || detail.error === 'pro-required')))
      );

      if (premiumGate) {
        const msg = (typeof detail === 'object' ? detail.message : null)
          || errorData?.message
          || 'SlushBook AI kræver et Light- eller Pro-abonnement. Opgrader for at lade AI\u2019en lave nye opskrifter for dig.';
        errorMessage = `🔒 ${msg}`;
      } else if (errorData?.error === 'need-more-info' || errorData?.fallback) {
        errorMessage = errorData.message || errorData.response || t('ai.needMoreInfo', 'Jeg mangler lige nogle detaljer. Kan du være mere specifik?');
      } else if (detail) {
        // `detail` may be a string OR a FastAPI 422 list-of-objects OR a plain
        // object. Stringifying directly produced "[object Object]" — humanise.
        let humanDetail = '';
        if (typeof detail === 'string') {
          humanDetail = detail;
        } else if (Array.isArray(detail)) {
          const tooLong = detail.find((d) => d?.type === 'string_too_long');
          if (tooLong) {
            humanDetail = 'Din søgning blev for lang. Prøv at gøre den kortere.';
          } else {
            humanDetail = detail
              .map((d) => (d && typeof d === 'object' ? d.msg || d.message : String(d)))
              .filter(Boolean)
              .join('. ');
          }
        } else if (typeof detail === 'object') {
          humanDetail = detail.message || detail.msg || JSON.stringify(detail);
        }
        errorMessage = `${humanDetail}${status ? ` (HTTP ${status})` : ''}`;
      } else if (status) {
        // Network/CORS/500 with no detail – at least show the status so production
        // debugging isn't a black box.
        errorMessage = `${errorMessage} (HTTP ${status} · ${currentTab.endpoint})`;
      } else if (error.message) {
        errorMessage = `${errorMessage} – ${error.message}`;
      }

      setMessages(prev => [...prev, {
        role: 'assistant',  // Changed from 'error' to 'assistant' for better UX
        content: errorMessage
      }]);
    } finally {
      setLoading(false);
    }
  };

  const formatStructuredResponse = (data) => {
    // Format different types of structured responses
    if (data.freeze_point_c !== undefined) {
      // Freeze curve response
      return `🌡️ Frysepunkt: ${data.freeze_point_c}°C\n📊 Konsistens: ${data.expected_consistency}\n⏱️ Frysetid: ~${data.estimated_freeze_time_minutes || 'N/A'} min`;
    } else if (data.alcohol_percent !== undefined) {
      // Alcohol optimization response
      return `🍹 Alkohol: ${data.alcohol_percent}%\n🌡️ Frysepunkt: ${data.freeze_point_c}°C\n✅ Kan fryse: ${data.is_freezable ? 'Ja' : 'Nej'}`;
    } else if (data.total_cost !== undefined) {
      // Cost calculation response
      return `💰 Total: ${data.total_cost} ${data.currency}\n📏 Pr. liter: ${data.cost_per_liter} ${data.currency}\n🥤 Pr. 200ml: ${data.cost_per_200ml} ${data.currency}\n🔢 Portioner: ${data.portions_200ml}`;
    }
    
    // Default formatting
    return JSON.stringify(data, null, 2);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleVoiceInput = () => {
    if (!recognition) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      try {
        recognition.start();
        setIsListening(true);
      } catch (error) {
        console.error('Failed to start recognition:', error);
        setIsListening(false);
      }
    }
  };

  const handleTourNext = () => {
    setCurrentTourStep(prev => prev + 1);
  };

  const handleTourSkip = () => {
    markTourCompleted(TOUR_KEYS.AI_ASSISTANT, API, updateCompletedTours);
    setCurrentTourStep(-1);
  };

  const handleTourFinish = () => {
    markTourCompleted(TOUR_KEYS.AI_ASSISTANT, API, updateCompletedTours);
    setCurrentTourStep(-1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white/95 backdrop-blur-lg rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden border border-white/20">
        {/* Header with SlushBook Gradient */}
        <div className="flex items-center justify-between px-4 py-3 text-white" style={{ background: 'linear-gradient(to right, #30B4E8, #8FD6B6, #FFE26D)' }}>
          <div className="flex items-center space-x-3">
            <img src="/icons/ai/64.png" alt="AI" style={{ width: '2.75rem', height: '2.75rem' }} />
            <div>
              <h2 className="text-xl font-bold">SlushBook AI</h2>
              <p className="text-sm opacity-90">{currentTab.label}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Dynamic Intro Area with Video/Color Background */}
        <div className="relative h-32 overflow-hidden border-b border-gray-200">
          {/* Video Background Option - 20% slower */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute top-0 left-0 w-full h-full object-cover"
            ref={(el) => {
              if (el) el.playbackRate = 0.8;
            }}
          >
            <source src="/slush-bg-video.mp4" type="video/mp4" />
          </video>
          
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/40"></div>
          
          {/* Text positioned at bottom */}
          <div className="absolute bottom-0 left-0 right-0 px-4 py-3">
            <p className="text-sm text-white flex items-center gap-2 drop-shadow-lg">
              <span className="text-lg">💡</span>
              <span className="font-medium">{t(currentTab.introKey || `ai.intro.${activeTab}`, currentTab.placeholder)}</span>
            </p>
          </div>
        </div>

        {/* Tabs with Unique Gradient Colors */}
        <div className="flex overflow-x-auto bg-gray-50 relative" style={{ scrollbarWidth: 'thin' }}>
          {availableTabs.map((tab) => {
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-gray-900 font-semibold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.icon}
                {tab.label}
                
                {/* Gradient top border for active tab */}
                {activeTab === tab.id && (
                  <div 
                    className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${tab.color}`}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-br from-gray-50 to-blue-50">
          {/* Guest preview banner — kept compact so it doesn't dominate the UI */}
          {isGuest && (
            <div
              className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="ai-guest-preview-banner"
            >
              <span className="text-lg leading-none">✨</span>
              <div className="flex-1">
                <div className="font-semibold">
                  {t('ai.guestBannerTitle', 'AI-assistent — Pro-funktion')}
                </div>
                <div className="text-amber-800 mt-0.5 text-xs">
                  {t(
                    'ai.guestBannerBody',
                    'Du kan udforske fanerne og se eksempler herunder. Send et spørgsmål for at se hvad AI’en kan — eller opgradér for at få rigtige svar.'
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowUpgrade(true)}
                data-testid="ai-guest-banner-cta"
                className="text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg whitespace-nowrap"
              >
                {t('ai.upgradeCTA', 'Se Pro')}
              </button>
            </div>
          )}

          {messages.length === 0 && (
            <div className="text-center text-gray-500 mt-12">
              <div className="text-6xl mb-4">{currentTab.icon}</div>
              <h3 className="text-xl font-semibold mb-2">{currentTab.label}</h3>
              <p className="text-sm">{t('ai.welcomeMessage', 'Stil mig et spørgsmål...')}</p>

              {/* Example prompts — clickable chips that prefill the input. For
                  guests this still works (and triggers the Pro-prompt on send) */}
              {currentTab.placeholder && (
                <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                  {(currentTab.examples || [currentTab.placeholder]).slice(0, 3).map((ex, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setInput(ex)}
                      data-testid={`ai-example-chip-${i}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700"
                    >
                      “{ex}”
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] p-4 rounded-xl shadow-sm ${
                  message.role === 'user'
                    ? 'bg-blue-500 text-white'
                    : message.role === 'error'
                    ? 'bg-red-100 text-red-800 border-l-4 border-red-500'
                    : 'bg-white text-gray-800 border border-gray-200'
                }`}
              >
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                  {message.content}
                </pre>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 p-4 rounded-xl shadow-sm flex items-center gap-3">
                <FaSpinner className="animate-spin text-blue-500 text-xl" />
                <span className="text-gray-600">AI tænker...</span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area with Voice Support */}
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={currentTab.placeholder}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows="2"
              />
              {/* Voice Input Button */}
              <button
                onClick={handleVoiceInput}
                className={`absolute right-2 top-2 p-2 rounded-lg transition ${
                  isListening
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={isListening ? 'Stop optagelse' : 'Start stemmeindtastning'}
              >
                <FaMicrophone className={isListening ? 'animate-pulse' : ''} />
              </button>
            </div>
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className={`px-5 py-3 rounded-lg font-medium transition flex items-center gap-2 ${
                input.trim() && !loading
                  ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <>
                  <FaPaperPlane className="text-sm" />
                  <span className="hidden sm:inline">{t('ai.send', 'Send')}</span>
                </>
              )}
            </button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-500">
              💡 {t('ai.input_hint')}
            </p>
            {isListening && (
              <p className="text-xs text-red-600 font-medium flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                {t('ai.listening')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* AI Assistant Tour Tooltip */}
      {currentTourStep >= 0 && (
        <OnboardingTooltip
          currentStep={currentTourStep}
          steps={getAiAssistantSteps()}
          onNext={handleTourNext}
          onSkip={handleTourSkip}
          onFinish={handleTourFinish}
        />
      )}

      {/* Upgrade prompt — opened when a guest tries to send a real prompt */}
      <UpgradeModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        reason="general"
      currentUser={user}
        requiredTier="light"
      />
    </div>
  );
};

export default AIMultiTabPopup;
